import { useEffect } from 'react';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { createLocalExpirableStorageProvider } from '@atlassian/jira-browser-storage-providers/src/controllers/local-storage/index.tsx';
import { useActiveIncidentsStore } from '../index.tsx';
import type { ActiveIncidentsState } from '../types.tsx';

export const STORAGE_PROVIDER_KEY = 'servicedesk-linked-object-incidents-store';
export const PERSIST_KEY = 'cross-tab-cache';
export const TIME_BEFORE_EXPIRY: number = 24 * 60 * 60 * 1000; // 1 day expiry so that old values (e.g. deleted services) don't stay in the cache forever.

const localExpirableStorageProvider = createLocalExpirableStorageProvider(STORAGE_PROVIDER_KEY);

type StorageErrorType = 'INITIAL_STATE' | 'SET_STATE' | 'SYNC_STATE' | 'ADD_EVENT_LISTENER';
const fireStorageError = (error: Error, errorType: StorageErrorType) => {
	// https://data-portal.internal.atlassian.com/analytics/registry/37613
	fireErrorAnalytics({
		meta: {
			id: 'crossTabCache',
			packageName: 'jiraServicedeskLinkedObjectIncidentsStore',
		},
		error,
		attributes: {
			errorType,
		},
		skipSentry: true,
	});
};

export const createInitialState = (): ActiveIncidentsState => {
	try {
		const cachedState = localExpirableStorageProvider.get(PERSIST_KEY);
		if (cachedState != null && typeof cachedState === 'object') {
			return cachedState;
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (error: any) {
		fireStorageError(error, 'INITIAL_STATE');
	}

	return {};
};

export const useCrossTabCache = (): void => {
	const [activeIncidents, { mergeActiveIncidents }] = useActiveIncidentsStore();

	// Store -> Local storage
	// Set state in local storage when the store is updated in the current tab.
	useEffect(() => {
		const isEmpty = Object.keys(activeIncidents).length === 0;
		const isLoading = Object.keys(activeIncidents).some(
			(serviceId) => activeIncidents[serviceId]?.loading,
		);
		const hasError = Object.keys(activeIncidents).some(
			(serviceId) => activeIncidents[serviceId]?.error,
		);

		if (isEmpty || isLoading || hasError) {
			return;
		}

		try {
			const expiry = Date.now() + TIME_BEFORE_EXPIRY;
			localExpirableStorageProvider.set(PERSIST_KEY, activeIncidents, expiry);
			localExpirableStorageProvider.removeExpiredKeys();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireStorageError(error, 'SET_STATE');
		}
	}, [activeIncidents]);

	// Local storage -> Store
	// Sync state from local storage when the the data is updated in another tab.
	useEffect(() => {
		const handleStorageEvent = ({ key, oldValue, newValue }: StorageEvent) => {
			if (key?.includes(`${STORAGE_PROVIDER_KEY}_${PERSIST_KEY}`)) {
				if (newValue == null || oldValue === newValue) {
					return;
				}

				try {
					const cachedState = JSON.parse(newValue);
					mergeActiveIncidents(cachedState);
					// eslint-disable-next-line @typescript-eslint/no-explicit-any
				} catch (error: any) {
					fireStorageError(error, 'SYNC_STATE');
				}
			}
		};

		try {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.addEventListener('storage', handleStorageEvent, false);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireStorageError(error, 'ADD_EVENT_LISTENER');
		}
	}, [mergeActiveIncidents]);
};
